import debug from 'debug';

const debugLog = debug('global:tracking:deferred');

/**
 * Hold the third-party half of an event until the integrations exist to receive it.
 *
 * While the banner is still open, `v4.calls` sends every event through
 * `firstPartyDispatch` — straight to our own origin, skipping analytics.js and therefore
 * every third-party integration. That is the whole point of the first-party path, but it
 * leaves a gap the moment the visitor then accepts: analytics.js loads, the Meta pixel (and
 * GTM, Google Ads, GA) initialise — and never hear about the page view that brought the
 * visitor here, because it was delivered before they existed and nothing re-sends it.
 *
 * CAP-4405: a visitor arriving from a Meta ad, accepting, and converting on the entry page
 * produced no `Page Viewed: start` for the pixel at all; the customer only saw the event
 * after a manual reload, which is the path where consent is already stored and the event
 * takes the normal analytics.js route.
 *
 * This restores what the analytics.js stub queue (`window.ps_queue`, drained by
 * `initializeWithIntegrations`) used to do for us: before CAP-4147 the pre-consent calls
 * went through the stub and were replayed to each newly activated integration. The
 * first-party path bypasses the stub, so the replay has to be staged explicitly.
 *
 * Only the pending → granted transition replays. After a rejection the queue is dropped
 * (see `purgeTrackingArtifacts`), so opting in later via "Manage Cookies" starts from that
 * moment rather than back-filling a visit the visitor had declined to share.
 */

/**
 * A funnel page has a handful of events; the cap only exists so a visitor who keeps
 * clicking through with the banner open cannot grow the queue without bound.
 */
const MAX_DEFERRED_EVENTS = 50;

let deferred: Array<() => void> = [];

/** Stage `send` to run against the integrations once they are initialised. */
export const deferToIntegrations = (send: () => void): void => {
    if (deferred.length >= MAX_DEFERRED_EVENTS) return;

    deferred.push(send);
};

/**
 * Deliver everything staged so far. Called from `initializeWithIntegrations`, after the
 * newly granted integrations are live and the stub queue has been drained.
 *
 * Snapshot first: a replayed call runs the normal (consented) path now, so it must not be
 * able to stage itself again.
 */
export const flushDeferredIntegrationEvents = (): void => {
    if (deferred.length === 0) return;

    const queued = deferred;
    deferred = [];

    debugLog('-> replaying %d event(s) to newly activated integrations', queued.length);

    queued.forEach((send) => {
        try {
            send();
        } catch (error) {
            // One destination must not cost the others their event. The page call is staged
            // before the `Page Viewed: <slug>` the pixel consumes, so a third-party script
            // throwing inside Segment's fan-out would otherwise drop exactly the event this
            // replay exists to deliver — and take the tail of the consent handler, which
            // dismisses the banner, with it.
            console.warn('deferred integration event failed', error);
        }
    });
};

/** Drop everything staged, without sending — the visitor rejected. */
export const clearDeferredIntegrationEvents = (): void => {
    deferred = [];
};
